import {
  buildGetCmaPresentationEditQueryKey,
  useGetCmaPresentationEdit,
} from "@cma/shared/queries/cma/useGetCmaPresentationEdit";
import { usePatchCma } from "@cma/shared/queries/cma/usePatchCma";
import { CMA_SCOPE_KEY } from "@cma/shared/queries/cma/cma.key";
import { useChatWidgetStore } from "@cma/store/chat-widget.store";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  PresentationSectionOnChangeHandler,
  EditablePresentationSourceData,
} from "./presentation-sections/PresentationSectionProps";
import { useDebouncedCallback } from "use-debounce";
import { toast } from "@shared/services/toast";
import { useUpdateEstimationPrice } from "@cma/shared/queries/cma/useUpdateEstimationPrice";
import { useUpdatePresentationConfig } from "@cma/shared/queries/cma/useUpdatePresentationConfig";
import { useFetchAllSectionsData } from "@cma/shared/queries/cma/useFetchAllSectionsData";
import { sectionWidgetRegistry } from "@cma/presentation-generation-logic/sections/section-widget-registry";
import { isNumber } from "@cma/shared/helpers/is-number";
import { useAddComparableProperty } from "@cma/shared/queries/cma/useAddComparableProperty";
import { useRemoveComparableProperty } from "@cma/shared/queries/cma/useRemoveComparableProperty";
import { useReorderComparableProperties } from "@cma/shared/queries/cma/useReorderComparableProperties";
import { useUpdateComparableProperty } from "@cma/shared/queries/cma/useUpdateComparableProperty";
import {
  buildPresentationView,
  ComparableProperty,
  PresentationEditData,
  PresentationSourceData,
  SectionConfig,
} from "@cma/presentation-generation-logic";
import { arrayMove } from "@dnd-kit/sortable";
import { useChainRequests } from "@cma/shared/hooks/useChainRequests";
import { buildListingMlsKey } from "@cma/shared/helpers/listing-mls-key";
import { v4 as uuidv4 } from "uuid";
import { useUIStore } from "@cma/store/ui.store";
import { diff } from "deep-object-diff";
import { PatchCMARecurringCostItemDto } from "@shared/apis/sidekick/generated";

type Props = {
  cmaId: string;
};

export type AddPresentationSectionOptions = {
  insertAfterIndex: number;
};

export type AddPresentationSectionActions = Partial<
  Record<
    SectionConfig["type"],
    (options: AddPresentationSectionOptions) => void
  >
>;

const patchSourceData = (
  current: PresentationSourceData,
  editable?: EditablePresentationSourceData,
): PresentationSourceData => {
  if (!editable) return current;
  const next = { ...current };

  if (editable.priceRange !== undefined) {
    next.priceRange = { ...current.priceRange, ...editable.priceRange };
  }
  if (editable.coverPhotoCdnUrl !== undefined) {
    next.coverPhotoCdnUrl = editable.coverPhotoCdnUrl;
  }
  if (editable.clientDisplayName !== undefined) {
    next.clientDisplayName = editable.clientDisplayName;
  }
  if (editable.featuredProperty) {
    const patch = Object.fromEntries(
      Object.entries(editable.featuredProperty).filter(
        ([, value]) => value !== undefined,
      ),
    );
    next.featuredProperty = { ...current.featuredProperty, ...patch };
  }

  return next;
};

export const usePresentationLogic = (props: Props) => {
  const { cmaId } = props;
  const queryClient = useQueryClient();
  const presentationQueryKey = buildGetCmaPresentationEditQueryKey(cmaId);

  const isChatWidgetStale = useChatWidgetStore(
    (state) => state.isChatWidgetStale,
  );
  const markChatWidgetFresh = useChatWidgetStore(
    (state) => state.markChatWidgetFresh,
  );
  const { addToPromiseChain } = useChainRequests();

  /**
   * Cancel any in-flight presentation queries before making an optimistic
   * cache update. This prevents a completing background refetch from
   * overwriting our local optimistic state.
   */
  const cancelPresentationQueries = useCallback(
    () => queryClient.cancelQueries({ queryKey: presentationQueryKey }),
    [queryClient, presentationQueryKey],
  );

  // Poll while any fetchable section still has fetchedAt == null (backend still processing).
  // Reading from cache avoids a forward-reference cycle: on every render triggered by the
  // query, the cache already has the latest data so shouldPoll is always up-to-date.
  const cachedPresentation =
    queryClient.getQueryData<PresentationEditData>(presentationQueryKey);
  const shouldPoll =
    cachedPresentation?.sections.some((section) => {
      const widget = sectionWidgetRegistry.get(section.config.type);
      if (!widget?.canFetchData) return false;
      if (
        !widget.canFetchData({
          config: section.config,
          presentationSourceData: cachedPresentation.sourceData,
        })
      )
        return false;
      return (
        (section.data as { fetchedAt?: Date | null } | null)?.fetchedAt == null
      );
    }) ?? false;

  const {
    isLoading,
    data: presentation,
    isError,
    error,
    refetch,
  } = useGetCmaPresentationEdit({ cmaId, pooling: shouldPoll });

  const presentationView = useMemo(
    () => (presentation ? buildPresentationView(presentation, true) : null),
    [presentation],
  );

  const invalidatePresentation = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: presentationQueryKey });
  }, [queryClient, presentationQueryKey]);

  useEffect(() => {
    if (isChatWidgetStale) {
      invalidatePresentation();
      markChatWidgetFresh();
    }
  }, [isChatWidgetStale, markChatWidgetFresh, invalidatePresentation]);

  // Clear cache on error so a stale presentation is never shown
  useEffect(() => {
    if (!isLoading && (isError || !cmaId)) {
      queryClient.removeQueries({ queryKey: presentationQueryKey });
    }
  }, [cmaId, isLoading, isError, queryClient, presentationQueryKey]);

  const patchCmaMutation = usePatchCma();
  const estimationPriceMutation = useUpdateEstimationPrice();
  const presentationConfigMutation = useUpdatePresentationConfig();
  const fetchAllSectionsDataMutation = useFetchAllSectionsData();

  const fetchAllSectionsData = useCallback(() => {
    if (!presentation) return;
    fetchAllSectionsDataMutation.mutate(presentation.id);
  }, [presentation, fetchAllSectionsDataMutation]);

  const savePartialSourceData = useCallback(
    (editableSourceData: EditablePresentationSourceData) => {
      const { clientDisplayName, priceRange, featuredProperty } =
        editableSourceData;

      if (
        editableSourceData?.clientDisplayName ||
        editableSourceData?.featuredProperty
      ) {
        patchCmaMutation.mutate({
          cmaId,
          clientDisplayName,
          subjectProperty: featuredProperty
            ? {
                neighborhood: featuredProperty.neighborhood,
                type: featuredProperty.propertyType,
                yearBuilt: featuredProperty.yearBuilt,
                livingAreaSqFt: featuredProperty.livingSpace,
                bedCount: featuredProperty.beds,
                bathCount: featuredProperty.baths,
                lotSizeSqFt: featuredProperty.lotSize,
                garageSpaces: featuredProperty.garageParkingSpaces,
                hoaFeeCents: isNumber(featuredProperty.hoaFees)
                  ? featuredProperty.hoaFees * 100
                  : featuredProperty.hoaFees,
                currentPrice: featuredProperty.currentPrice,
                soldPrice: featuredProperty.soldPrice,
                hasDoormanAttendance: featuredProperty.hasDoormanAttendance,
                roomsTotal: featuredProperty.roomsTotal,
                flipTaxRemarks: featuredProperty.flipTaxRemarks,
                recurringCosts: parseRecurringCosts(featuredProperty),
                notes: featuredProperty.notes,
              }
            : undefined,
        });
      }

      if (
        priceRange &&
        !(
          isNumber(priceRange.max, { positive: true }) &&
          isNumber(priceRange.min, { positive: true }) &&
          priceRange.min > priceRange.max
        )
      ) {
        estimationPriceMutation.mutate({
          cmaId,
          priceMin: priceRange.min,
          priceMax: priceRange.max,
        });
      }
    },
    [patchCmaMutation, cmaId, estimationPriceMutation],
  );

  const previousDataRef = useRef<PresentationEditData | undefined>();

  const saveSectionConfigWithDebounce = useDebouncedCallback(
    () => {
      if (!presentation) return;

      addToPromiseChain({
        id: "section-config-batch",
        type: "add",
        execute: async () => {
          await cancelPresentationQueries();
          const currentData = queryClient.getQueryData(presentationQueryKey) as
            | PresentationEditData
            | undefined;

          return presentationConfigMutation
            .mutateAsync({
              id: presentation.id,
              sectionsConfig:
                currentData?.sections.map((s) => s.config) ||
                presentation.sections.map((s) => s.config),
            })
            .catch(() => {
              if (previousDataRef.current) {
                queryClient.setQueryData(
                  presentationQueryKey,
                  previousDataRef.current,
                );
              }
            });
        },
      });
    },
    500,
    { leading: false, trailing: true },
  );

  const addSaveSectionConfigToPromiseChain = useCallback(
    (previousData?: PresentationEditData) => {
      if (!presentation) return;
      previousDataRef.current = previousData;
      saveSectionConfigWithDebounce();
    },
    [presentation, saveSectionConfigWithDebounce],
  );

  const updateSection: PresentationSectionOnChangeHandler = useCallback(
    (params) => {
      if (!presentation) return;

      const { sectionConfig, editableSourceData } = params;

      const section = presentation.sections.find(
        (s) => s.config.id === sectionConfig.id,
      )!;
      const widget = sectionWidgetRegistry.get(section.config.type);
      const sanitizedSectionConfig =
        widget && widget.sanitizeSectionConfig
          ? widget.sanitizeSectionConfig(sectionConfig)
          : sectionConfig;

      const previousData = queryClient.getQueryData(presentationQueryKey) as
        | PresentationEditData
        | undefined;

      cancelPresentationQueries();

      queryClient.setQueryData(
        presentationQueryKey,
        (old: PresentationEditData) => {
          if (!old) return old;
          return {
            ...old,
            sections: old.sections.map((s) =>
              s.config.id === sectionConfig.id
                ? { ...s, config: sanitizedSectionConfig }
                : s,
            ),
            sourceData: patchSourceData(old.sourceData, editableSourceData),
          };
        },
      );

      if (editableSourceData) {
        savePartialSourceData(editableSourceData);
      }

      const sectionWidget = sectionWidgetRegistry.get(sectionConfig.type);
      const shouldInvalidateData = sectionWidget?.shouldInvalidateData?.({
        prevConfig: section.config,
        nextConfig: sanitizedSectionConfig,
        sectionData: section.data,
      });

      if (shouldInvalidateData) {
        if (
          !sectionWidget?.canFetchData ||
          !sectionWidget.canFetchData({
            config: sanitizedSectionConfig,
            presentationSourceData: presentation.sourceData,
          })
        ) {
          console.error("Cannot fetch data for this section.");
          return;
        }
        addSaveSectionConfigToPromiseChain(previousData);
        saveSectionConfigWithDebounce.flush();
        addToPromiseChain({
          id: "fetch-all-sections-data",
          type: "add",
          execute: async () => {
            fetchAllSectionsDataMutation.mutate(presentation.id);
          },
        });
        return;
      }

      const configDiff = diff(sectionConfig, section.config);
      const hasSectionConfigChanges = Object.keys(configDiff).length > 0;
      if (hasSectionConfigChanges) {
        addSaveSectionConfigToPromiseChain(previousData);
      }
    },
    [
      presentation,
      queryClient,
      presentationQueryKey,
      cancelPresentationQueries,
      savePartialSourceData,
      addSaveSectionConfigToPromiseChain,
      addToPromiseChain,
      fetchAllSectionsDataMutation,
      saveSectionConfigWithDebounce,
    ],
  );

  const chatWidgetConfirmDialog = useUIStore(
    (state) => state.chatWidgetConfirmDialog,
  );

  const deleteSection = useCallback(
    async (id: string) => {
      if (!presentation) return;

      const shouldDelete = await chatWidgetConfirmDialog({
        title: "Delete Section",
        content: "Are you sure you want to delete this section?",
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
      });
      if (!shouldDelete) return;

      const previousData = queryClient.getQueryData(presentationQueryKey) as
        | PresentationEditData
        | undefined;

      const sectionsUpdate = presentation.sections.filter(
        (section) => section.config.id !== id,
      );

      cancelPresentationQueries();

      queryClient.setQueryData(
        presentationQueryKey,
        (old: PresentationEditData) => {
          if (!old) return old;
          return { ...old, sections: sectionsUpdate };
        },
      );

      addToPromiseChain({
        id: `delete-section-${id}`,
        type: "add",
        execute: () =>
          presentationConfigMutation
            .mutateAsync({
              id: presentation.id,
              sectionsConfig: sectionsUpdate.map((s) => s.config),
            })
            .catch(() => {
              if (previousData) {
                queryClient.setQueryData(presentationQueryKey, previousData);
              }
            }),
      });
    },
    [
      presentation,
      chatWidgetConfirmDialog,
      queryClient,
      presentationQueryKey,
      cancelPresentationQueries,
      addToPromiseChain,
      presentationConfigMutation,
    ],
  );

  const reorderSections = useCallback(
    (oldIndex: number, newIndex: number) => {
      if (!presentation) return;

      const previousData = queryClient.getQueryData(presentationQueryKey) as
        | PresentationEditData
        | undefined;

      const sectionsUpdate = arrayMove(
        presentation.sections,
        oldIndex,
        newIndex,
      );

      cancelPresentationQueries();

      queryClient.setQueryData(
        presentationQueryKey,
        (old: PresentationEditData) => {
          if (!old) return old;
          return { ...old, sections: sectionsUpdate };
        },
      );

      addToPromiseChain({
        id: `reorder-sections`,
        type: "add",
        execute: () =>
          presentationConfigMutation
            .mutateAsync({
              id: presentation.id,
              sectionsConfig: sectionsUpdate.map((s) => s.config),
            })
            .catch(() => {
              if (previousData) {
                queryClient.setQueryData(presentationQueryKey, previousData);
              }
            }),
      });
    },
    [
      presentation,
      queryClient,
      presentationQueryKey,
      cancelPresentationQueries,
      addToPromiseChain,
      presentationConfigMutation,
    ],
  );

  const addNewSection = useCallback(
    (sectionConfig: SectionConfig, options: AddPresentationSectionOptions) => {
      if (!presentation) return;

      const previousData = queryClient.getQueryData(presentationQueryKey) as
        | PresentationEditData
        | undefined;

      const section = {
        type: sectionConfig.type,
        config: { ...sectionConfig, id: uuidv4() },
        data: null,
      };

      const insertPosition = Math.min(
        Math.max(0, options.insertAfterIndex + 1),
        presentation.sections.length,
      );

      const sectionsUpdate = [...presentation.sections];
      sectionsUpdate.splice(insertPosition, 0, section);

      cancelPresentationQueries();

      queryClient.setQueryData(
        presentationQueryKey,
        (old: PresentationEditData) => {
          if (!old) return old;
          return { ...old, sections: sectionsUpdate };
        },
      );

      addToPromiseChain({
        id: `add-section-${section.config.id}`,
        type: "add",
        execute: () =>
          presentationConfigMutation
            .mutateAsync({
              id: presentation.id,
              sectionsConfig: sectionsUpdate.map((s) => s.config),
            })
            .catch(() => {
              if (previousData) {
                queryClient.setQueryData(presentationQueryKey, previousData);
              }
            }),
      });

      const widget = sectionWidgetRegistry.get(section.config.type);
      const shouldFetch =
        !!widget?.canFetchData &&
        widget.canFetchData({
          config: section.config,
          presentationSourceData: presentation.sourceData,
        });

      if (shouldFetch) {
        addToPromiseChain({
          id: "fetch-all-sections-data",
          type: "add",
          execute: async () => {
            fetchAllSectionsDataMutation.mutate(presentation.id);
          },
        });
      }
    },
    [
      presentation,
      queryClient,
      presentationQueryKey,
      cancelPresentationQueries,
      addToPromiseChain,
      presentationConfigMutation,
      fetchAllSectionsDataMutation,
    ],
  );

  const addAIAssistedTextSection = useCallback(
    (options: AddPresentationSectionOptions) => {
      const widget = sectionWidgetRegistry.get("ai-assisted-text");
      if (!widget || !presentation) return;
      addNewSection(
        widget.buildDefaultConfig({ sourceData: presentation.sourceData }),
        options,
      );
    },
    [addNewSection, presentation],
  );

  const addBrandingSection = useCallback(
    (options: AddPresentationSectionOptions) => {
      const widget = sectionWidgetRegistry.get("branding");
      if (!widget || !presentation) return;
      addNewSection(
        widget.buildDefaultConfig({ sourceData: presentation.sourceData }),
        options,
      );
    },
    [addNewSection, presentation],
  );

  const addTeamBrandingSection = useCallback(
    (options: AddPresentationSectionOptions) => {
      const widget = sectionWidgetRegistry.get("team-branding");
      if (!widget || !presentation) return;
      addNewSection(
        widget.buildDefaultConfig({ sourceData: presentation.sourceData }),
        options,
      );
    },
    [addNewSection, presentation],
  );

  const addSectionActions: AddPresentationSectionActions = useMemo(() => {
    const hasTeamBrandingSection = presentation?.sections.some(
      (s) => s.config.type === "team-branding",
    );
    return {
      "ai-assisted-text": addAIAssistedTextSection,
      branding: addBrandingSection,
      ...(!hasTeamBrandingSection && {
        "team-branding": addTeamBrandingSection,
      }),
    };
  }, [
    addAIAssistedTextSection,
    addBrandingSection,
    addTeamBrandingSection,
    presentation,
  ]);

  const addComparablePropertyMutation = useAddComparableProperty();
  const removeComparablePropertyMutation = useRemoveComparableProperty();
  const reorderComparablePropertiesMutation = useReorderComparableProperties();
  const updateComparablePropertyMutation = useUpdateComparableProperty();

  const handleAddComparableProperty = useCallback(
    (comparableProperty: ComparableProperty) => {
      if (!presentation) return;

      const previousData = queryClient.getQueryData(presentationQueryKey) as
        | PresentationEditData
        | undefined;

      comparableProperty.id = `temp-${uuidv4()}`;

      cancelPresentationQueries();

      queryClient.setQueryData(
        presentationQueryKey,
        (old: PresentationEditData) => {
          if (!old) return old;
          const propertyExists = old.sourceData.comparableProperties.some(
            (p) =>
              p.mlsId === comparableProperty.mlsId &&
              p.mlsSource === comparableProperty.mlsSource,
          );
          if (propertyExists) return old;
          return {
            ...old,
            sourceData: {
              ...old.sourceData,
              comparableProperties: [
                ...old.sourceData.comparableProperties,
                comparableProperty,
              ],
              updatedAt: new Date(),
            },
          };
        },
      );

      addToPromiseChain({
        id: buildListingMlsKey({
          mlsId: comparableProperty.mlsId!,
          mlsSource: comparableProperty.mlsSource!,
        }),
        type: "add",
        execute: () =>
          addComparablePropertyMutation
            .mutateAsync({
              cmaId,
              mlsId: comparableProperty.mlsId!,
              mlsSource: comparableProperty.mlsSource!,
            })
            .then(() => {
              invalidatePresentation();
            })
            .catch(() => {
              if (previousData) {
                queryClient.setQueryData(presentationQueryKey, previousData);
              }
            }),
      });
    },
    [
      presentation,
      addComparablePropertyMutation,
      cmaId,
      addToPromiseChain,
      queryClient,
      presentationQueryKey,
      cancelPresentationQueries,
      invalidatePresentation,
    ],
  );

  const handleRemoveComparableProperty = useCallback(
    (comparableProperty: ComparableProperty) => {
      if (!presentation) return;

      const previousData = queryClient.getQueryData(presentationQueryKey) as
        | PresentationEditData
        | undefined;

      cancelPresentationQueries();

      queryClient.setQueryData(
        presentationQueryKey,
        (old: PresentationEditData) => {
          if (!old) return old;
          return {
            ...old,
            sourceData: {
              ...old.sourceData,
              comparableProperties: old.sourceData.comparableProperties.filter(
                (prop) =>
                  !(
                    prop.mlsId === comparableProperty.mlsId &&
                    prop.mlsSource === comparableProperty.mlsSource
                  ),
              ),
              updatedAt: new Date(),
            },
          };
        },
      );

      addToPromiseChain({
        id: buildListingMlsKey({
          mlsId: comparableProperty.mlsId!,
          mlsSource: comparableProperty.mlsSource!,
        }),
        type: "remove",
        execute: () =>
          removeComparablePropertyMutation
            .mutateAsync({
              cmaId,
              mlsId: comparableProperty.mlsId!,
              mlsSource: comparableProperty.mlsSource!,
            })
            .catch(() => {
              if (previousData) {
                queryClient.setQueryData(presentationQueryKey, previousData);
              }
            }),
      });
    },
    [
      presentation,
      removeComparablePropertyMutation,
      cmaId,
      addToPromiseChain,
      queryClient,
      presentationQueryKey,
      cancelPresentationQueries,
    ],
  );

  const handleUpdateComparableProperty = useCallback(
    (comparableProperty: ComparableProperty) => {
      if (!presentation) return;

      const previousData = queryClient.getQueryData(presentationQueryKey) as
        | PresentationEditData
        | undefined;

      cancelPresentationQueries();

      queryClient.setQueryData(
        presentationQueryKey,
        (old: PresentationEditData) => {
          if (!old) return old;
          return {
            ...old,
            sourceData: {
              ...old.sourceData,
              comparableProperties: old.sourceData.comparableProperties.map(
                (prop) =>
                  prop.mlsId === comparableProperty.mlsId &&
                  prop.mlsSource === comparableProperty.mlsSource
                    ? { ...prop, notes: comparableProperty.notes }
                    : prop,
              ),
              updatedAt: new Date(),
            },
          };
        },
      );

      addToPromiseChain({
        id: buildListingMlsKey({
          mlsId: comparableProperty.mlsId,
          mlsSource: comparableProperty.mlsSource,
        }),
        type: "update",
        execute: () =>
          updateComparablePropertyMutation
            .mutateAsync({
              cmaId,
              mlsId: comparableProperty.mlsId,
              mlsSource: comparableProperty.mlsSource,
              notes: comparableProperty.notes ?? null,
            })
            .then(() => {
              invalidatePresentation();
            })
            .catch(() => {
              if (previousData) {
                queryClient.setQueryData(presentationQueryKey, previousData);
              }
            }),
      });
    },
    [
      presentation,
      updateComparablePropertyMutation,
      cmaId,
      addToPromiseChain,
      queryClient,
      presentationQueryKey,
      cancelPresentationQueries,
      invalidatePresentation,
    ],
  );

  const handleReorderComparableProperties = useCallback(
    (comparableProperties: ComparableProperty[]) => {
      if (!presentation) return;

      const previousData = queryClient.getQueryData(presentationQueryKey) as
        | PresentationEditData
        | undefined;

      cancelPresentationQueries();

      queryClient.setQueryData(
        presentationQueryKey,
        (old: PresentationEditData) => {
          if (!old) return old;
          return {
            ...old,
            sourceData: {
              ...old.sourceData,
              comparableProperties,
              updatedAt: new Date(),
            },
          };
        },
      );

      const order = comparableProperties
        .filter((p) => p.mlsId && p.mlsSource)
        .map((p) => ({ mlsId: p.mlsId!, mlsSource: p.mlsSource! }));

      reorderComparablePropertiesMutation
        .mutateAsync({ cmaId, order })
        .then(() => {
          invalidatePresentation();
        })
        .catch(() => {
          if (previousData) {
            queryClient.setQueryData(presentationQueryKey, previousData);
          }
        });
    },
    [
      presentation,
      reorderComparablePropertiesMutation,
      cmaId,
      queryClient,
      presentationQueryKey,
      cancelPresentationQueries,
      invalidatePresentation,
    ],
  );

  const isMutating = useIsMutating({ mutationKey: [CMA_SCOPE_KEY] }) > 0;
  const isSaving = saveSectionConfigWithDebounce.isPending() || isMutating;
  const isSynching = isLoading || isSaving;

  const updatePresentationTheme = useCallback(
    ({ themeId, onSuccess }: { themeId: string; onSuccess?: () => void }) => {
      if (!presentation) return;
      addToPromiseChain({
        id: "presentation-theme",
        type: "add",
        execute: async () => {
          await cancelPresentationQueries();
          const currentData =
            queryClient.getQueryData<PresentationEditData>(
              presentationQueryKey,
            );
          queryClient.setQueryData<PresentationEditData>(
            presentationQueryKey,
            (old) => (old ? { ...old, themeId } : old),
          );
          try {
            const result = await presentationConfigMutation.mutateAsync({
              id: presentation.id,
              sectionsConfig:
                currentData?.sections.map((s) => s.config) ??
                presentation.sections.map((s) => s.config),
              themeId,
            });
            onSuccess?.();
            return result;
          } catch (err) {
            queryClient.setQueryData(presentationQueryKey, currentData);
            toast.error("Failed to save presentation theme.");
            throw err;
          }
        },
      });
    },
    [
      presentation,
      presentationConfigMutation,
      queryClient,
      presentationQueryKey,
      cancelPresentationQueries,
      addToPromiseChain,
    ],
  );

  return {
    presentation,
    presentationView,
    error,
    isLoading,
    isSynching,
    isFetchingAllSectionsData: fetchAllSectionsDataMutation.isPending,
    addSectionActions,
    updateSection,
    deleteSection,
    reorderSections,
    refetchPresentation: refetch,
    handleAddComparableProperty,
    handleRemoveComparableProperty,
    handleUpdateComparableProperty,
    handleReorderComparableProperties,
    invalidatePresentation,
    fetchAllSectionsData,
    updatePresentationTheme,
  };
};

function parseRecurringCosts(
  featuredProperty: EditablePresentationSourceData["featuredProperty"],
): PatchCMARecurringCostItemDto[] | undefined {
  const legacyHoaFeesAsRecorruringFees = isNumber(featuredProperty?.hoaFees)
    ? {
        label: "HOA Fees",
        value: featuredProperty.hoaFees,
        type: "hoa-fees",
        frequency: "monthly",
      }
    : undefined;
  const hoaFeeAsRecorruringFees = isNumber(featuredProperty?.hoaFee?.fee)
    ? {
        label: "HOA Fee",
        value: featuredProperty.hoaFee.fee,
        type: "hoa-fee",
        frequency: featuredProperty.hoaFee.frequency,
      }
    : undefined;
  if (legacyHoaFeesAsRecorruringFees || hoaFeeAsRecorruringFees) {
    return undefined;
  }
  const parsedRecurringCosts: PatchCMARecurringCostItemDto[] = [];
  if (legacyHoaFeesAsRecorruringFees) {
    parsedRecurringCosts.push(legacyHoaFeesAsRecorruringFees);
  }
  if (hoaFeeAsRecorruringFees) {
    parsedRecurringCosts.push(hoaFeeAsRecorruringFees);
  }
  return parsedRecurringCosts;
}
